"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

type Props = {
  src: string;
  /** Whether the video should play when it is on screen; true by default. */
  playing?: boolean;
  className?: string;
  /** Names the video for assistive tech; without one it is decorative. */
  label?: string;
};

/**
 * A muted, looping video that fills its parent. It fetches nothing until it is
 * first asked to play on screen, plays only while it is on screen, and pauses
 * when it scrolls away or is switched off.
 */
export default function BackgroundVideo({
  src,
  playing = true,
  className,
  label,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !("IntersectionObserver" in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(true);

      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.25 },
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  const shouldPlay = playing && isVisible;

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (!shouldPlay) {
      video.pause();

      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasStarted(true);
    video.play()?.catch(() => {});
  }, [shouldPlay, hasStarted]);

  return (
    <video
      ref={videoRef}
      className={clsx("absolute inset-0 size-full object-cover", className)}
      src={hasStarted ? src : undefined}
      muted
      loop
      playsInline
      preload="none"
      aria-label={label}
      aria-hidden={label ? undefined : true}
    ></video>
  );
}
